import { useState, useEffect, useCallback, useMemo, useRef, type ElementType } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MessageSquare,
  Link2,
  FileText,
  ArrowUpFromLine,
  LogIn,
  PlugZap,
  X,
  RefreshCw,
  FolderOpen,
} from 'lucide-react'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { Button } from '@/components/ui/button'
import { SidebarListRow } from '@/components/layout/sidebar/SidebarListRow'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { SidebarEmptyState } from '@/components/layout/sidebar/SidebarEmptyState'
import { SidebarErrorState } from '@/components/layout/sidebar/SidebarErrorState'
import { SidebarListSkeleton } from '@/components/layout/sidebar/SidebarListSkeleton'
import { SidebarFilterBar } from '@/components/layout/sidebar/SidebarFilterBar'
import { DriveFileTab } from './drive-file-tab'
import {
  clampDriveFocusWorkspaceId,
  resolveDriveFocusCandidates,
  shouldShowDriveFocusPicker,
} from './drive-focus-workspace'
import {
  DRIVE_BROWSE_SCOPE_EVENT,
  DRIVE_MAIN_SURFACE_EVENT,
  dispatchDriveBrowseScope,
  dispatchDriveFocusWorkspace,
  dispatchDriveMainSurface,
  getLastDriveMainSurface,
  type DriveMainSurfaceDetail,
} from './drive-browse-events'
import { WorkspaceScopeSelector } from '@/components/layout/workspace-scope-selector'
import {
  GOOGLE_DRIVE_IMPORT_COMPLETE_EVENT,
  openGoogleDriveImportPicker,
} from './gdoc-import-utils'
import { useWorkspace } from '@/providers/workspace-provider'
import { fetchWithAuth } from '@/lib/api/core'
import { useGoogleDriveAuth } from '@/hooks/use-google-drive-auth'
import {
  fetchLinkedGoogleDocuments,
  syncGoogleDoc,
  type LinkedGoogleDocument,
} from '@/lib/google-drive-sync-api'
import {
  consumeLegacyGoogleDriveErrorParam,
  DRIVE_ACCESS_COPY,
  isGoogleDriveAccessBlockedError,
  openGoogleDriveOAuthPopup,
  requestGoogleDriveAccess,
} from '@/lib/google-drive-access'
import type { EntityContextMenuEntry } from '@/components/features/entity-context-menu'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'
import { documentUrlForPathOrId } from '@/lib/app-routes'
import { formatDriveRelativeDate } from './drive-tree-adapter'
import { wsNavSectionStyle } from '@/lib/ws-nav'
import type { OpenTab } from '@/components/features/document-tabs'
import {
  buildOpenTabIndex,
  isOpenInTabs,
  type OpenTabIndex,
} from '@/lib/open-tab-match'

// ??? Types ????????????????????????????????????????????????????????????????????

type DriveTab = 'drive' | 'comments' | 'linked'

// ??? Helpers ??????????????????????????????????????????????????????????????????

function formatRelativeDate(iso?: string): string {
  return formatDriveRelativeDate(iso)
}

// ??? Linked Tab ???????????????????????????????????????????????????????????????

function LinkedTab({
  dense = false,
  organizationId,
  organizationIds,
  onOpenLinkedDocument,
  openTabIndex,
  activeTabId = null,
}: {
  dense?: boolean
  organizationId?: string
  /** Active workspaces - show linked docs across these orgs (not just the first). */
  organizationIds?: string[]
  /** Open the Maater synced doc (prefer over Drive preview). */
  onOpenLinkedDocument?: (documentId: string) => void
  openTabIndex?: OpenTabIndex
  activeTabId?: string | null
}) {
  const navigate = useNavigate()
  const [docs, setDocs] = useState<LinkedGoogleDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [syncingId, setSyncingId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const linked = await fetchLinkedGoogleDocuments({
        scope: 'mine',
        limit: 100,
      })
      setDocs(linked)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load linked docs')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    const onImportComplete = () => { void load() }
    window.addEventListener(GOOGLE_DRIVE_IMPORT_COMPLETE_EVENT, onImportComplete)
    return () => window.removeEventListener(GOOGLE_DRIVE_IMPORT_COMPLETE_EVENT, onImportComplete)
  }, [load])

  const handleSync = useCallback(async (doc: LinkedGoogleDocument) => {
    if (syncingId) return
    setSyncingId(doc.id)
    try {
      const result = await syncGoogleDoc({
        documentId: doc.id,
        organizationId: organizationId ?? doc.organizationId,
      })
      if (result.status === 'diverged') {
        toast.message(`"${doc.title}" has conflicting changes`, {
          description: 'Open the document to review Sync.',
        })
      } else {
        toast.success(`Synced "${doc.title}"`)
      }
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Sync failed')
    } finally {
      setSyncingId(null)
    }
  }, [syncingId, organizationId, load])

  const filtered = search.trim()
    ? docs.filter(
        (d) =>
          d.title.toLowerCase().includes(search.toLowerCase()) ||
          d.gdocId.toLowerCase().includes(search.toLowerCase()),
      )
    : docs

  if (loading) return <SidebarListSkeleton rows={5} dense={dense} />

  if (error) {
    return <SidebarErrorState message={error} onRetry={load} />
  }

  return (
    <>
      <SidebarFilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search linked docs..."
        searchTestId={testIds.driveSidebar.linkedSearch}
      />
      {filtered.length === 0 ? (
        <SidebarEmptyState
          icon={<Link2 className="h-8 w-8" />}
          title="No linked documents"
          description="Publish to Drive, import a Google Doc, or open a doc with Keep synced - your linked Maater docs show here."
          data-testid={testIds.driveSidebar.linkedEmpty}
        />
      ) : (
        <div className="flex-1 overflow-auto" data-testid={testIds.driveSidebar.linkedList}>
          {filtered.map((doc) => {
            const openLinked = () => {
              // Prefer tab open via Browse-style onDocumentClick. Bare navigate('/doc/...')
              // is often a no-op against URL sync (active explorer /doc tab, rate limits).
              if (onOpenLinkedDocument) {
                onOpenLinkedDocument(doc.id)
                return
              }
              navigate(documentUrlForPathOrId(doc.id))
            }
            const contextMenuEntries: EntityContextMenuEntry[] = [
              {
                label: 'Open in Maater',
                icon: FileText,
                onSelect: openLinked,
              },
              {
                label: syncingId === doc.id ? 'Syncing...' : 'Sync with Drive',
                icon: ArrowUpFromLine,
                disabled: Boolean(syncingId),
                onSelect: () => void handleSync(doc),
              },
            ]
            const isActiveDoc =
              activeTabId === doc.id || activeTabId === `doc-${doc.id}`
            const isOpenDoc =
              !isActiveDoc &&
              !!openTabIndex &&
              isOpenInTabs(openTabIndex, { documentId: doc.id })
            return (
              <SidebarListRow
                key={doc.id}
                dense={dense}
                state={{ active: isActiveDoc, open: isOpenDoc }}
                contextMenuEntries={contextMenuEntries}
                onActivate={openLinked}
                data-testid={testIds.driveSidebar.linkedItem(doc.id)}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <p className="truncate text-sm font-medium">{doc.title}</p>
                  </div>
                  {!dense && (
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {doc.gdocSyncMode === 'keep_synced' ? 'Keep synced' : 'Linked'}
                      {doc.gdocLastSynced
                        ? ` · ${formatRelativeDate(doc.gdocLastSynced)}`
                        : ''}
                    </p>
                  )}
                </div>
              </SidebarListRow>
            )
          })}
        </div>
      )}
    </>
  )
}

// ??? Not-connected / auth-error state ????????????????????????????????????????

function DriveNotConnected({
  onConnect,
  onRequestAccess,
  needsReauth = false,
  accessBlocked = false,
  accountEmail,
  requestingAccess = false,
}: {
  onConnect: () => void
  onRequestAccess: () => void
  needsReauth?: boolean
  accessBlocked?: boolean
  accountEmail?: string | null
  requestingAccess?: boolean
}) {
  if (accessBlocked) {
    return (
      <SidebarEmptyState
        icon={<GoogleDriveIcon className="h-8 w-8" />}
        title={DRIVE_ACCESS_COPY.blockedTitle}
        description={DRIVE_ACCESS_COPY.blockedBody}
        action={
          <div className="flex flex-col items-stretch gap-2 w-full max-w-[220px]">
            <Button
              variant="default"
              size="sm"
              onClick={onRequestAccess}
              disabled={requestingAccess}
              data-testid={testIds.driveSidebar.requestAccess}
            >
              {requestingAccess ? 'Requesting…' : DRIVE_ACCESS_COPY.requestAccessLabel}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onConnect}
              data-testid={testIds.driveSidebar.retryConnect}
            >
              {DRIVE_ACCESS_COPY.retryConnectLabel}
            </Button>
          </div>
        }
        data-testid={testIds.driveSidebar.notConnected}
      />
    )
  }

  return (
    <SidebarEmptyState
      icon={<GoogleDriveIcon className="h-8 w-8" />}
      title={needsReauth ? DRIVE_ACCESS_COPY.reconnectTitle : DRIVE_ACCESS_COPY.notConnectedTitle}
      description={
        needsReauth
          ? `This workspace needs full Drive access${accountEmail ? ` for ${accountEmail}` : ''}. Disconnect is not required - reconnect and approve Drive permissions when Google asks.`
          : DRIVE_ACCESS_COPY.notConnectedBody
      }
      action={
        <div className="flex flex-col items-stretch gap-2 w-full max-w-[220px]">
          <Button
            variant="outline"
            size="sm"
            onClick={onConnect}
            data-testid={testIds.driveSidebar.retryConnect}
          >
            {needsReauth ? DRIVE_ACCESS_COPY.reconnectTitle : 'Connect Google Drive'}
          </Button>
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={onRequestAccess}
            data-testid={testIds.driveSidebar.needAccessLink}
          >
            {DRIVE_ACCESS_COPY.needAccessLabel}
          </button>
        </div>
      }
      data-testid={testIds.driveSidebar.notConnected}
    />
  )
}

// ??? Main DriveSidebar ????????????????????????????????????????????????????????

const TABS: { id: DriveTab; label: string; Icon: ElementType }[] = [
  { id: 'drive', label: 'Drive', Icon: GoogleDriveIcon },
  { id: 'comments', label: 'Activity', Icon: MessageSquare },
  { id: 'linked', label: 'Linked', Icon: Link2 },
]

export function DriveSidebar({
  dense = false,
  organizationId,
  organizationIds,
  onOpenDrivePreview,
  onOpenLinkedDocument,
  onOpenFolderView,
  openTabs = [],
  activeTabId = null,
}: {
  dense?: boolean
  /** Fallback org when the Drive focus picker has not resolved yet. */
  organizationId?: string
  /** Active workspace IDs for Linked + Activity lists and Drive account candidates. */
  organizationIds?: string[]
  onOpenDrivePreview?: (
    file: { id: string; name: string; mimeType: string; webViewLink?: string },
    options: { organizationId: string; event?: React.MouseEvent; importedDocumentId?: string },
  ) => void
  /** Open a Linked Maater document (synced doc preferred over Drive preview). */
  onOpenLinkedDocument?: (documentId: string) => void
  /** Open the Drive folder view tab in the main panel. */
  onOpenFolderView?: () => void
  openTabs?: OpenTab[]
  activeTabId?: string | null
}) {
  const { workspaces, activeWorkspaceIds } = useWorkspace()
  const focusActiveIds = organizationIds?.length ? organizationIds : activeWorkspaceIds
  const openTabIndex = useMemo(() => buildOpenTabIndex(openTabs), [openTabs])
  const openDriveSurfaces = useMemo(() => {
    const surfaces = new Set<DriveTab>()
    for (const tab of openTabs) {
      if (activeTabId && tab.id === activeTabId) continue
      if (
        tab.type === 'integration-google-drive' ||
        tab.type === 'integration-google-drive-preview'
      ) {
        surfaces.add('drive')
      }
      if (
        tab.type === 'integration-google-drive-preview' &&
        tab.integrationData?.importedDocumentId
      ) {
        surfaces.add('linked')
      }
    }
    return surfaces
  }, [openTabs, activeTabId])
  const driveFocusCandidates = useMemo(
    () => resolveDriveFocusCandidates(focusActiveIds, workspaces),
    [focusActiveIds, workspaces],
  )
  const [focusOrgId, setFocusOrgId] = useState(() =>
    clampDriveFocusWorkspaceId(organizationId ?? '', driveFocusCandidates),
  )
  const showDriveFocusPicker = shouldShowDriveFocusPicker(driveFocusCandidates)

  useEffect(() => {
    setFocusOrgId((prev) =>
      clampDriveFocusWorkspaceId(prev || organizationId || '', driveFocusCandidates),
    )
  }, [driveFocusCandidates, organizationId])

  useEffect(() => {
    if (focusOrgId) dispatchDriveFocusWorkspace(focusOrgId)
  }, [focusOrgId])

  const { authStatus, isLoading: authLoading } = useGoogleDriveAuth(focusOrgId || undefined)
  const linkedOrgIds = organizationIds?.length
    ? organizationIds
    : organizationId
      ? [organizationId]
      : undefined
  const [activeTab, setActiveTab] = useState<DriveTab>(() => {
    const surface = getLastDriveMainSurface()
    if (surface === 'activity') return 'comments'
    if (surface === 'linked') return 'linked'
    return 'drive'
  })
  const [authError, setAuthError] = useState(false)
  const [accessBlocked, setAccessBlocked] = useState(false)
  const [oauthErrorCode, setOauthErrorCode] = useState<string | null>(null)
  const [requestingAccess, setRequestingAccess] = useState(false)

  useEffect(() => {
    const onSurface = (event: Event) => {
      const surface = (event as CustomEvent<DriveMainSurfaceDetail>).detail?.surface
      if (surface === 'activity') setActiveTab('comments')
      else if (surface === 'linked') setActiveTab('linked')
      else if (surface === 'drive') setActiveTab('drive')
    }
    const onScope = () => setActiveTab('drive')
    window.addEventListener(DRIVE_MAIN_SURFACE_EVENT, onSurface)
    window.addEventListener(DRIVE_BROWSE_SCOPE_EVENT, onScope)
    return () => {
      window.removeEventListener(DRIVE_MAIN_SURFACE_EVENT, onSurface)
      window.removeEventListener(DRIVE_BROWSE_SCOPE_EVENT, onScope)
    }
  }, [])

  const fallbackImportOrgId = focusOrgId || organizationId || authStatus?.organizationId || workspaces[0]?.id || ''

  const connected = authStatus?.connected ?? false
  const needsReauth = Boolean(authStatus?.needsReauth || (connected && authStatus?.hasDriveScope === false))

  const openImportPicker = useCallback(
    (opts?: { organizationId?: string }) => {
      if (!connected || needsReauth) return
      const orgId = opts?.organizationId || fallbackImportOrgId
      if (!orgId) return
      openGoogleDriveImportPicker({ organizationId: orgId })
    },
    [connected, needsReauth, fallbackImportOrgId],
  )

  // Legacy ?google_drive_error= and popup oauth error events
  useEffect(() => {
    const legacy = consumeLegacyGoogleDriveErrorParam()
    if (legacy) {
      setOauthErrorCode(legacy)
      setAuthError(true)
      if (isGoogleDriveAccessBlockedError(legacy)) setAccessBlocked(true)
      toast.error(
        isGoogleDriveAccessBlockedError(legacy)
          ? DRIVE_ACCESS_COPY.blockedTitle
          : 'Google Drive connection failed',
        {
          description: isGoogleDriveAccessBlockedError(legacy)
            ? DRIVE_ACCESS_COPY.blockedBody
            : legacy,
        },
      )
    }
    const onOauthError = (event: Event) => {
      const detail = (event as CustomEvent<{ error?: string }>).detail
      const code = detail?.error || 'access_denied'
      setOauthErrorCode(code)
      setAuthError(true)
      if (isGoogleDriveAccessBlockedError(code)) setAccessBlocked(true)
    }
    window.addEventListener('google-drive-auth-error', onOauthError)
    return () => window.removeEventListener('google-drive-auth-error', onOauthError)
  }, [])

  // Reset auth error once the connection is (re-)established with Drive scope
  useEffect(() => {
    if (connected && !needsReauth) {
      setAuthError(false)
      setAccessBlocked(false)
      setOauthErrorCode(null)
    }
  }, [connected, needsReauth])

  const prevFocusOrgIdRef = useRef(focusOrgId)
  useEffect(() => {
    if (prevFocusOrgIdRef.current === focusOrgId) return
    prevFocusOrgIdRef.current = focusOrgId
    setAuthError(false)
    setAccessBlocked(false)
    setOauthErrorCode(null)
  }, [focusOrgId])

  const handleAuthError = useCallback(() => setAuthError(true), [])

  const handleConnect = useCallback(async () => {
    if (!focusOrgId) {
      toast.error('Select a workspace before connecting Google Drive')
      return
    }
    try {
      await openGoogleDriveOAuthPopup(focusOrgId)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start Google Drive connection')
    }
  }, [focusOrgId])

  const handleRequestAccess = useCallback(async () => {
    setRequestingAccess(true)
    try {
      const result = await requestGoogleDriveAccess({
        organizationId: focusOrgId,
        errorCode: oauthErrorCode,
      })
      toast.success(
        result.alreadyRequested ? 'Access already requested' : 'Access requested',
        { description: result.message },
      )
      setAccessBlocked(true)
    } catch (err) {
      toast.error('Could not request access', {
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setRequestingAccess(false)
    }
  }, [focusOrgId, oauthErrorCode])

  const showNotConnected =
    !authLoading && (!connected || authError || needsReauth || accessBlocked)
  const focusWsColor = workspaces.find((w) => w.id === focusOrgId)?.color ?? null

  return (
    <div
      className="flex h-full flex-col min-h-0"
      style={wsNavSectionStyle(focusWsColor)}
      data-testid={testIds.driveSidebar.container}
    >
      <nav
        className="shrink-0 space-y-0.5 px-1 py-2"
        data-testid={testIds.driveSidebar.surfaceNav}
      >
        {TABS.map(({ id, label, Icon }) => (
          <SidebarTreeRow
            key={id}
            padLeft={20}
            active={activeTab === id}
            open={activeTab !== id && openDriveSurfaces.has(id)}
            icon={<Icon className="h-3.5 w-3.5" />}
            label={label}
            className="cursor-pointer"
            data-testid={testIds.driveSidebar.tab(id)}
            onClick={() => {
              setActiveTab(id)
              const orgId = focusOrgId || fallbackImportOrgId || undefined
              if (id === 'drive') {
                dispatchDriveMainSurface({ workspaceId: orgId, surface: 'drive' })
                return
              }
              if (id === 'comments') {
                dispatchDriveMainSurface({ workspaceId: orgId, surface: 'activity' })
                return
              }
              if (id === 'linked') {
                dispatchDriveMainSurface({ workspaceId: orgId, surface: 'linked' })
              }
            }}
          />
        ))}
      </nav>

      {showDriveFocusPicker ? (
        <div className="shrink-0 space-y-1 px-2 pb-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Drive account
          </p>
          <WorkspaceScopeSelector
            mode="single"
            candidateIds={driveFocusCandidates}
            scope={focusOrgId ? [focusOrgId] : null}
            onScopeChange={(scope) => {
              if (scope?.[0]) {
                setFocusOrgId(scope[0])
                dispatchDriveBrowseScope({
                  workspaceId: scope[0],
                  viewKind: 'myDrive',
                })
              }
            }}
            fallbackScope={driveFocusCandidates[0] ? [driveFocusCandidates[0]] : null}
            label="Drive account"
            description="Each workspace has its own Google login."
            tooltip="Google account for this Drive tree"
            triggerClassName="w-full justify-between"
            triggerTestId={testIds.driveSidebar.workspaceSelect}
          />
        </div>
      ) : null}

      {/* Content */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {authLoading ? (
          <SidebarListSkeleton rows={5} dense={dense} />
        ) : showNotConnected ? (
          <DriveNotConnected
            onConnect={handleConnect}
            onRequestAccess={handleRequestAccess}
            needsReauth={needsReauth || (authError && !accessBlocked)}
            accessBlocked={accessBlocked}
            accountEmail={authStatus?.accountEmail}
            requestingAccess={requestingAccess}
          />
        ) : (
          <>
            <DriveFileTab
              dense={dense}
              fill={activeTab !== 'linked'}
              organizationId={focusOrgId}
              onAuthError={handleAuthError}
              onOpenDrivePreview={onOpenDrivePreview}
              onOpenImportPicker={openImportPicker}
              onOpenFolderView={onOpenFolderView}
            />
            {activeTab === 'linked' && (
              <div className="min-h-0 flex-1 overflow-hidden border-t border-border">
                <LinkedTab
                  dense={dense}
                  organizationId={focusOrgId}
                  organizationIds={linkedOrgIds}
                  onOpenLinkedDocument={onOpenLinkedDocument}
                  openTabIndex={openTabIndex}
                  activeTabId={activeTabId}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
